import { Type } from '@angular/core';
import type { FilterStateSlot } from '../../state/rates-filter-state.service';
import type { PickerComponent } from './picker.types';

/**
 * Menu-axis Phase 3e — picker registry.
 *
 * Maps each filter dimension (chip key) to:
 *   · the picker component that edits it
 *   · the display label shown in the popover header
 *   · the FilterStateSlot field the picker reads/writes
 *   · the menu group for the `+ Add filter` dropdown
 *
 * 13 tab-agnostic dimensions are listed. Booleans (rop / bail / bonus /
 * inverse) have NO entry -- they're toggle pills (Phase 3f), not
 * popovers.
 *
 * 'company' (carrier) is excluded -- it lives on the component, not
 * the slot. Phase 3f or a dedicated patch ships its picker.
 *
 * Entries are populated incrementally across Phase 3e sub-tasks:
 *   3e.2 — FlatChecklist (8 dimensions)
 *   3e.3 — StateGrid (1 dimension: state)
 *   3e.5 — Range + Numeric (issue-age, contribution)
 */

export type PickerGroup = 'Carrier' | 'Geography' | 'Strategy' | 'Features';

export interface PickerRegistryEntry {
  component: Type<PickerComponent>;
  label: string;
  field: keyof FilterStateSlot;
  group: PickerGroup;
}

// 3e.1 ships an empty registry. Sub-task 3e.2 adds the FlatChecklist
// entries, 3e.3 adds the state entry, 3e.5 adds Range + Numeric.
//
// Importers should treat the registry as opaque (use registry-lookup
// helpers) so future re-exports stay backward-compatible.
export const pickerRegistry: Record<string, PickerRegistryEntry> = {};

/** Returns the registry entry for a dimension or undefined if no
 *  picker is registered (e.g. boolean dimensions, or pre-3e.2 state). */
export function lookupPicker(dimensionKey: string): PickerRegistryEntry | undefined {
  return pickerRegistry[dimensionKey];
}

// groupedPickerKeys() removed in Phase 11.7 — it only fed the retired
// +Add filter menu. The `group` field on PickerRegistryEntry is now
// unused metadata; left in place to avoid touching every picker's
// self-registration (harmless).
